import { useComponentRegistry } from '@bem-react/di';
import React, { Fragment } from 'react';

import {
    IOpenGraph,
    ISolution,
    ISolutionSection,
    TFiltersCategory,
    TLevel
} from 'client/common/types';
import cn from 'utils/cn';
import { reachGoal } from 'utils/metrika';
import { getTags } from 'utils/solutions';

import './index.css';

export interface ISolutionPageProps {
    solution: ISolution;
    solutionsUrl: string;
    filter: Pick<ISolutionSection, TFiltersCategory>;
    keywords?: string;
    levels?: TLevel[];
    shareServices: string[];
    language?: string;
}

const CATEGORIES: TFiltersCategory[] = ['tasks', 'industries', 'products', 'companySize'];

export const cnSolutionInfo = cn('solution-info');

function SolutionPage({
    keywords,
    solution,
    solutionsUrl,
    filter,
    levels,
    shareServices,
    language
}: ISolutionPageProps) {
    const { MainLayout, Level, YaShare, Media } = useComponentRegistry('app');

    const {
        slug,
        title,
        date,
        description,
        shareText,
        topShareGoalId,
        bottomShareGoalId
    } = solution;

    const tags = getTags(solution, {
        filter,
        categories: CATEGORIES
    })
        .map(({ slug: tagSlug, name, category }) => (
            <a
                key={tagSlug}
                className={cnSolutionInfo('tag')}
                href={`${solutionsUrl}?${category}=${tagSlug}`}
                >
                {name}
            </a>
        ));

    return (
        <Fragment>
            <MainLayout transparent={false}>
                <Media
                    language={language}
                    className={cnSolutionInfo()}
                    slug={slug}
                    keywords={keywords}
                    title={title}
                    dateString={date}
                    description={description}
                    shareServices={shareServices}
                    addonBefore={<div className={cnSolutionInfo('tags')}>{tags}</div>}
                    shareGoalId={topShareGoalId}
                    >
                    {levels && (
                        <div className={cnSolutionInfo('levels')} itemProp="articleBody">
                            {levels.map((level, index) => {
                                let tagName;

                                if (level.type === 'results' && level.tag) {
                                    const { tag: rawTag } = level;
                                    const [category, tagSlug] = rawTag.split(':');

                                    const foundedFilter = filter[category as TFiltersCategory]
                                        .find(el => el && el.slug === tagSlug);

                                    tagName = (foundedFilter && foundedFilter.name) || '';
                                }

                                const { og } = level;

                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <React.Fragment key={`${level.order}-${index}`}>
                                        <Level
                                            className={cnSolutionInfo('level')}
                                            {...level}
                                            tag={tagName}
                                            />
                                        {og && Object
                                            .keys(og)
                                            .map((key: keyof IOpenGraph) => og[key])
                                            .some(Boolean) && (
                                            <YaShare
                                                id={`block-${level.order}`}
                                                blockOrder={level.order}
                                                className={cnSolutionInfo('share')}
                                                popup
                                                size="m"
                                                services={shareServices}
                                                onShare={() => reachGoal(level.shareGoalId)}
                                                />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                    <div className={cnSolutionInfo('share-bottom')}>
                        {shareText && (
                            <div className={cnSolutionInfo('share-text')}>
                                {shareText}
                            </div>
                        )}
                        <YaShare
                            id={'share-bottom'}
                            className={cnSolutionInfo('share')}
                            size="m"
                            services={shareServices}
                            onShare={() => reachGoal(bottomShareGoalId)}
                            />
                    </div>
                </Media>
            </MainLayout>
        </Fragment>
    );
}

export default SolutionPage;
